/**
 * StudyBuddyAdmin — /admin/study-buddy. Nightly match runs at 08:00 UTC.
 * Admins can dry-run, force-run, and browse recent matches here.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import ToolSelector from '../../components/ToolSelector';
import {
  ArrowLeft, Users2, Play, FlaskConical, Loader2, CalendarClock, Info,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const StudyBuddyAdmin = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const fetchMatches = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/study-buddy/matches?days=30`);
      setMatches(data?.matches || []);
    } catch { toast.error('Could not load matches'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMatches(); }, []); // eslint-disable-line

  const run = async (dryRun) => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/study-buddy/run-match?dry_run=${dryRun ? 'true' : 'false'}`);
      setLastRun(data);
      toast.success(
        dryRun
          ? `Dry run — ${data.matches_made} match${data.matches_made === 1 ? '' : 'es'} would be created`
          : `Matched ${data.matches_made} pair${data.matches_made === 1 ? '' : 's'} and DMs sent`
      );
      if (!dryRun) fetchMatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Match run failed');
    } finally { setRunning(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="study-buddy-admin-page">
      <ToolSelector />
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-3 rounded-full h-8 text-xs">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Admin
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <span className="eyebrow">Admin · Community</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
              Study Buddy matcher
            </h1>
            <p className="text-muted-foreground text-sm max-w-2xl mt-1">
              Nightly pairs learners active on the same lesson in overlapping timezones and with
              compatible confidence levels — then DMs each of them a friendly intro. Runs
              automatically at <strong>08:00 UTC</strong> daily.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => run(true)}
              disabled={running}
              className="rounded-full font-bold"
              data-testid="dry-run-btn"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5 mr-1" />}
              Dry run
            </Button>
            <Button
              onClick={() => run(false)}
              disabled={running}
              className="rounded-full font-bold"
              data-testid="force-run-btn"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              Run now
            </Button>
          </div>
        </div>

        {lastRun && (
          <div className="pro-card p-4 mb-6 grid gap-3 sm:grid-cols-4" data-testid="last-run-panel">
            <Stat label="Candidates" value={lastRun.candidates_seen} />
            <Stat label="Lessons scanned" value={lastRun.lessons_scanned} />
            <Stat label="Matches made" value={lastRun.matches_made} />
            <Stat label="TZ skipped" value={lastRun.matches_skipped_tz + lastRun.matches_skipped_cooldown + lastRun.matches_skipped_optout} />
          </div>
        )}

        <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
          Recent matches (last 30 days)
        </h2>
        {loading ? (
          <div className="text-muted-foreground text-sm inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> loading…
          </div>
        ) : matches.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
            <Info className="w-4 h-4" /> No matches yet — will populate after the first nightly run.
          </div>
        ) : (
          <div className="pro-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Lesson</th>
                  <th className="text-left p-3">Users</th>
                  <th className="text-left p-3 w-24">DM sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {matches.map((m) => (
                  <tr key={m.id} data-testid={`match-row-${m.id}`}>
                    <td className="p-3 text-xs text-muted-foreground">
                      <CalendarClock className="w-3 h-3 inline mr-1" />
                      {(() => { try { return formatDistanceToNow(parseISO(m.created_at), { addSuffix: true }); } catch { return m.created_at; } })()}
                    </td>
                    <td className="p-3 font-mono text-xs">{m.lesson_id?.slice(0, 12)}…</td>
                    <td className="p-3">
                      <div className="inline-flex items-center gap-1">
                        <Users2 className="w-3 h-3 text-primary" />
                        <span className="font-mono text-[11px] truncate">{m.user_a?.slice(0, 6)}… ↔ {m.user_b?.slice(0, 6)}…</span>
                      </div>
                    </td>
                    <td className="p-3">
                      {m.dm_sent
                        ? <span className="inline-flex items-center rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Sent</span>
                        : <span className="inline-flex items-center rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-slate-500/15 text-slate-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
    <div className="font-heading text-xl font-extrabold">{value}</div>
  </div>
);

export default StudyBuddyAdmin;
